import React, { useEffect, useMemo, useState } from "react";
import "../styles/contacto.css";
import { Email, Phone, AccessTime, LocationOn } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { publicApi } from "../services/api";

const Contacto = () => {
  const location = useLocation();
  const isSolicitudRegistroSocio = useMemo(
    () => location.pathname === "/solicitud-registro-socios",
    [location.pathname]
  );
  const motivoRegistroSocio = "Registro de socios";
  const getInitialFormData = (isSolicitud) => ({
    nombre: "",
    correo: "",
    telefono: "",
    motivo: isSolicitud ? motivoRegistroSocio : "",
    mensaje: "",
  });

  const [formData, setFormData] = useState(getInitialFormData(isSolicitudRegistroSocio));

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    // Reinicia el formulario al cambiar entre Contacto y Solicitud Registro Socios
    setFormData(getInitialFormData(isSolicitudRegistroSocio));
    setMessage({ type: "", text: "" });
  }, [isSolicitudRegistroSocio]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    // Validación básica
    if (!formData.nombre.trim()) {
      setMessage({ type: "error", text: "El nombre es obligatorio" });
      return;
    }

    if (!formData.correo.trim() || !formData.correo.includes("@")) {
      setMessage({ type: "error", text: "El correo electrónico es inválido" });
      return;
    }

    if (!isSolicitudRegistroSocio && !formData.motivo.trim()) {
      setMessage({ type: "error", text: "Por favor selecciona un motivo" });
      return;
    }

    if (
      !isSolicitudRegistroSocio &&
      (!formData.mensaje.trim() || formData.mensaje.trim().length < 10)
    ) {
      setMessage({
        type: "error",
        text: "El mensaje debe tener al menos 10 caracteres",
      });
      return;
    }

    setLoading(true);

    try {
      const endpoint = isSolicitudRegistroSocio
        ? "/contacto/solicitud-registro-socio/"
        : "/contacto/";
      const payload = isSolicitudRegistroSocio
        ? {
            nombre: formData.nombre,
            correo: formData.correo,
            telefono: formData.telefono,
          }
        : formData;

      const response = await publicApi.post(endpoint, payload);
      const data = response.data;
      if (response.status >= 200 && response.status < 300) {
        setMessage({
          type: "success",
          text:
            data.message ||
            "Mensaje enviado exitosamente. Te responderemos pronto.",
        });
        // Limpiar formulario
        setFormData({
          ...getInitialFormData(isSolicitudRegistroSocio),
        });
      }
    } catch (error) {
      const errorData = error?.response?.data || {};
      const flatErrors =
        errorData?.errors && typeof errorData.errors === "object"
          ? Object.values(errorData.errors).flat().join(", ")
          : "";
      setMessage({
        type: "error",
        text:
          errorData?.message ||
          errorData?.detail ||
          flatErrors ||
          "Error al enviar el mensaje. Por favor intenta nuevamente.",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="contacto-contenedor">
      {/* === Callout superior === */}
      <section className="contacto-callout">
        <h4>{isSolicitudRegistroSocio ? "Solicitud Registro Socios" : "Contacto"}</h4>
        <p>
          {isSolicitudRegistroSocio
            ? "Completa tus datos y enviaremos tu solicitud de registro de socio."
            : "Envíanos un mensaje y te responderemos en menos de 24 horas."}
        </p>
      </section>
      {/* === Contenedor de bloques === */}
      <div className="contacto-bloques">
        {/* Bloque 1 - Escríbenos */}
        <section className="contacto-bloque-escribenos">
          <h3>Escríbenos</h3>
          {/* Mensajes de éxito/error */}
          {message.text && (
            <div className={`mensaje-${message.type}`}>{message.text}</div>
          )}
          <form className="contacto-formulario" onSubmit={handleSubmit}>
            <div className="fila">
              <div className="campo">
                <label htmlFor="nombre">Nombre:</label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="campo">
                <label htmlFor="correo">Correo Electrónico:</label>
                <input
                  type="email"
                  id="correo"
                  name="correo"
                  value={formData.correo}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="fila">
              <div className="campo">
                <label htmlFor="telefono">Teléfono (opcional):</label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="campo">
                <label htmlFor="motivo">Motivo:</label>
                <input
                  list={isSolicitudRegistroSocio ? undefined : "motivos"}
                  id="motivo"
                  name="motivo"
                  value={formData.motivo}
                  onChange={handleChange}
                  disabled={loading || isSolicitudRegistroSocio}
                  readOnly={isSolicitudRegistroSocio}
                />
                {!isSolicitudRegistroSocio && (
                  <datalist id="motivos">
                    <option value="Información" />
                    <option value="Inscripción a Cursos" />
                    <option value="Ayuda con Trámite" />
                  </datalist>
                )}
              </div>
            </div>

            {isSolicitudRegistroSocio ? (
              <div className="contacto-nota-registro">
                Para agilizar tu trámite de afiliación, por favor ten lista tu fotografía tamaño 3x3 con fondo azul.
                El proceso de validación y respuesta puede demorar hasta 10 días hábiles.
              </div>
            ) : (
              <div className="campo campo-textarea">
                <label htmlFor="mensaje">Mensaje:</label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows="6"
                  value={formData.mensaje}
                  onChange={handleChange}
                  disabled={loading}
                ></textarea>
              </div>
            )}

            <button type="submit" disabled={loading}>
              {loading ? "Enviando..." : "Enviar mensaje"}
            </button>
          </form>
        </section>
        <div className="contacto-columna-derecha">
          {/* Bloque 2 - Información */}
          <section className="contacto-bloque-info">
            <h3>Información</h3>
            <p>
              <Email /> abislapazcorrespondencia@gmail.com
            </p>
            <p>
              <Phone /> +591 69696230
            </p>
            <p>
              <AccessTime /> Horario: 14:30 - 19:00
            </p>
          </section>
          {/* Bloque 3 - Ubicación */}
          <section className="contacto-bloque-ubicacion">
            <h3>Ubicación</h3>
            <div>
              <LocationOn /> Av. 20 de octubre 1915, Edif. Malaga - Primer Piso
              <div style={{ width: "100%", height: "350px" }}>
                <iframe
                  title="Av. 20 de octubre 1915, Edif. Malaga - Primer Piso"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.41080177021!2d-68.13642611198622!3d-16.50534574360102!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x915f207b2c1ad8a5%3A0x7515d2caf49f48cc!2sEdificio%20MALAGA%2C%20Av.20%20de%20Octubre%2C%20La%20Paz!5e0!3m2!1ses-419!2sbo!4v1763191415100!5m2!1ses-419!2sbo"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: "12px" }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Contacto;
